/**
 * What the customer said about a finished job.
 *
 * Seed data and pure helpers, standing in for a reviews API. The composer that
 * writes these is `src/components/review/ReviewSheet.tsx`; the list that reads
 * them back is `src/screens/account/RatingsScreen.tsx`.
 *
 * ---------------------------------------------------------------------------
 * ONE REVIEW PER BOOKING
 * ---------------------------------------------------------------------------
 * A review is keyed by `bookingId`, not by an id of its own, because that is
 * the rule the backend will enforce and the UI has to agree with it. Rating a
 * booking twice is editing the first review, and the screen says so -
 * "Edit your review" rather than "Rate & Review" - so nobody discovers the
 * rule by having a submit rejected.
 *
 * ---------------------------------------------------------------------------
 * PHOTOS ARE THE CUSTOMER'S, NOT THE PROFESSIONAL'S
 * ---------------------------------------------------------------------------
 * These never merge with a professional's job-proof photographs. Different
 * author, different trust level: proof is evidence the work happened, a review
 * photo is an opinion about how it looks. Keeping them apart is why `hidden`
 * lives on the photo rather than on some shared gallery item.
 */

import { IconName } from '../components/ui/Icon';
/* Read at call time by the two helpers below - never at module scope, which
   runs at launch before the config request has landed. */
import { appConfig } from '../services/config';

/**
 * How many photographs one review may carry.
 *
 * ---------------------------------------------------------------------------
 * IT WAS FIVE HERE AND THREE ON THE SERVER
 * ---------------------------------------------------------------------------
 * The note that stood here called this "a platform setting in everything but
 * name" and said the app should read it from a settings payload. It was right,
 * and until it did, the two disagreed: `REVIEW_SETTINGS.maxPhotos` has
 * defaulted to 3 since reviews existed and the server enforces it. This app
 * invited a customer to attach five photographs, two of which were never going
 * to arrive.
 *
 * Served now. The constant below is what the strip uses before the config
 * lands and matches the SERVER's default - deliberately not the five this file
 * used to say, which would have kept the bug alive for anyone the config never
 * reached.
 */
const FALLBACK_MAX_PHOTOS = 3;

/**
 * The cap, read at call time.
 *
 * A function rather than a constant because it comes from the server now, and
 * a value read once at import would freeze whatever the app compiled with.
 * The strip's counter, the add tile's disabled state and the picker's limit
 * all go through this.
 */
export const reviewMaxPhotos = (): number =>
  appConfig().reviewMaxPhotos ?? FALLBACK_MAX_PHOTOS;

/** How long a review stays editable after the visit. */
export const REVIEW_EDIT_DAYS = 30;

/**
 * Longest a comment may run.
 *
 * Not a technical limit - it is the length past which nobody reads a review.
 * The counter only appears in the last fifty characters, so it is a warning
 * rather than a target.
 *
 * Served too, and client-only: the composer stops here, the API does not.
 */
const FALLBACK_MAX_CHARS = 500;

export const reviewMaxChars = (): number =>
  appConfig().reviewMaxChars ?? FALLBACK_MAX_CHARS;

export type ReviewPhoto = {
  id: string;
  /** Local file URI from the picker, or a remote URL once uploaded. */
  uri: string;
  /**
   * Taken down by the customer who posted it.
   *
   * Hidden rather than deleted: the review it belongs to still counts, and the
   * takedown is recorded rather than the row vanishing. See US-10.10 - this is
   * regret about showing your own living room, not a complaint, so it needs no
   * moderation queue and no explanation.
   */
  hidden?: boolean;
};

export type MyReview = {
  /** The booking this is about. One review per booking, so this is the key. */
  bookingId: string;
  /** Denormalised for the list, which never loads bookings. */
  service: string;
  icon: IconName;
  when: string;
  /** 1–5. Zero is not a rating, it is an unsubmitted form. */
  stars: number;
  comment: string;
  /** Keys from `REVIEW_TAGS`, not free text - see below. */
  tags: string[];
  photos: ReviewPhoto[];
  /** Set when the review has been changed since it was first left. */
  edited?: boolean;
};

/**
 * The chips offered under the stars.
 *
 * ---------------------------------------------------------------------------
 * THE VALUES ARE THE SERVER'S, THE WORDS ARE THE CUSTOMER'S
 * ---------------------------------------------------------------------------
 * A fixed vocabulary, for the reason this file has always given: a tag is only
 * worth anything if it can be COUNTED across reviews, and "punctual",
 * "Punctual" and "on time!!" cannot be. Free words go in the comment.
 *
 * What changed is whose vocabulary it is. This list used to be the app's own,
 * split by trade - "Spotless finish" for a cleaner, "Explained the fault" for
 * an electrician - and none of those words exists on the server, which
 * validates every tag against a closed list and refuses the whole request on
 * one it does not know. So every review with a chip selected came back 400 and
 * the customer was told "Your review was not saved". Only a review with no
 * chips at all ever landed.
 *
 * The trade split had to go with it, and losing it costs less than it looks:
 * the server has no category dimension to count against, so a per-trade tag
 * could never be aggregated into "this Pro is punctual" - which is the entire
 * point of having chips rather than free text.
 *
 * `key` goes on the wire and must match the server exactly. `label` is what a
 * person reads and can be reworded freely.
 */
export type ReviewTag =
  | 'punctual'
  | 'polite'
  | 'clean_work'
  | 'well_equipped'
  | 'explained_clearly'
  | 'late'
  | 'unprepared'
  | 'rushed';

export const REVIEW_TAGS: ReadonlyArray<{ key: ReviewTag; label: string }> = [
  { key: 'punctual', label: 'Punctual' },
  { key: 'polite', label: 'Polite' },
  { key: 'clean_work', label: 'Clean work' },
  { key: 'well_equipped', label: 'Well equipped' },
  { key: 'explained_clearly', label: 'Explained clearly' },
  { key: 'late', label: 'Late' },
  { key: 'unprepared', label: 'Unprepared' },
  { key: 'rushed', label: 'Rushed' },
];

/**
 * The words for a stored key.
 *
 * Falls back to the key itself rather than to nothing: a review written on an
 * older build may carry a tag this list no longer names, and showing the raw
 * value is honest where showing a blank chip is not.
 */
export const labelForTag = (key: string): string =>
  REVIEW_TAGS.find(tag => tag.key === key)?.label ?? key;

/** Photographs still on show - a hidden one is not drawn anywhere. */
export const visiblePhotos = (review: MyReview): ReviewPhoto[] =>
  review.photos.filter(photo => !photo.hidden);

/**
 * The counters this review moves on the professional's profile.
 *
 * Here rather than in a screen because it is the arithmetic an edit has to
 * undo: a rating changed from 5 to 3 is `+3 −5` on the sum and nothing at all
 * on the count. Getting that wrong is how a professional's average drifts a
 * tenth at a time, and it is invisible until somebody totals it by hand.
 */
export const ratingDelta = (
  next: number,
  previous?: number,
): { sum: number; count: number } => ({
  sum: next - (previous ?? 0),
  count: previous === undefined ? 1 : 0,
});
